import { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { io, Socket } from 'socket.io-client';
import { getConfigOrThrow, selectUser } from 'gossamer-front-end';
import type { UnreadStatusEvent } from './socketTypes';

/**
 * Hook for real-time messaging notifications via Socket.io (Admin Frontend)
 *
 * Connects to backend Socket.io server and receives push notifications
 * when new messages arrive or read marks are updated.
 *
 * Replaces polling-based approach with true real-time updates.
 */
export function useSocketMessaging() {
  const user = useSelector(selectUser);
  const [unread, setUnread] = useState<boolean>(false);
  const [connected, setConnected] = useState<boolean>(false);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!user?.userId) {
      // Disconnect if user logs out
      if (socketRef.current) {
        console.log('[Socket.io] User logged out, disconnecting...');
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setUnread(false);
      setConnected(false);
      return;
    }

    console.log('[Socket.io] Initializing connection for user:', user.userId);

    const backendOrigin = getConfigOrThrow('gossamer-be-origin');
    console.log('[Socket.io] Connecting to:', backendOrigin);

    // Create socket connection
    const socket = io(backendOrigin, {
      path: '/socket.io/',
      auth: {
        userId: user.userId,
      },
      transports: ['websocket', 'polling'], // WebSocket preferred, polling fallback
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    socketRef.current = socket;

    // Connection events
    socket.on('connect', () => {
      console.log('[Socket.io] Connected:', socket.id);
      setConnected(true);

      // Request current unread status on connect
      socket.emit('get_unread_status');

      // Request notification permission (if not already granted)
      if ('Notification' in window && Notification.permission === 'default') {
        console.log('[Notifications] Requesting permission...');
        Notification.requestPermission().then((permission) => {
          console.log('[Notifications] Permission:', permission);
        });
      }
    });

    socket.on('disconnect', (reason) => {
      console.log('[Socket.io] Disconnected:', reason);
      setConnected(false);
    });

    socket.on('connect_error', (error) => {
      console.error('[Socket.io] Connection error:', error.message);
      setConnected(false);
    });

    socket.on('reconnect', (attemptNumber) => {
      console.log('[Socket.io] Reconnected after', attemptNumber, 'attempts');
      setConnected(true);
      // Request fresh status after reconnect
      socket.emit('get_unread_status');
    });

    socket.on('reconnect_failed', () => {
      console.error('[Socket.io] Reconnection failed after all attempts');
      setConnected(false);
    });

    // Unread status updates
    socket.on('unread_status', (data: UnreadStatusEvent) => {
      console.log('[Socket.io] Unread status update:', data.unread);
      setUnread(data.unread);

      // Update Service Worker badge (if available)
      if ('setAppBadge' in navigator) {
        if (data.unread) {
          (navigator as any).setAppBadge(1).catch((err: Error) => {
            console.warn('[Badge] Failed to set app badge:', err);
          });
        } else {
          (navigator as any).clearAppBadge().catch((err: Error) => {
            console.warn('[Badge] Failed to clear app badge:', err);
          });
        }
      }

      // Notify Service Worker with rich notification data (if registered)
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'UNREAD_STATUS',
          unread: data.unread,
          notification: data.notification,
        });
      }

      // Show browser notification if app is in background and we have notification data
      if (data.notification && document.hidden && 'Notification' in window && Notification.permission === 'granted') {
        const notificationData = data.notification;
        const { convoTitle, senderName, messagePreview } = notificationData;
        const notificationTitle = `${senderName} • ${convoTitle}`;
        const notificationOptions: NotificationOptions = {
          body: messagePreview || 'New message',
          icon: '/assets/other/web-app-manifest-192x192.png',
          badge: '/assets/other/web-app-manifest-192x192.png',
          tag: `convo-${notificationData.convoId}`,
          requireInteraction: false,
          data: {
            convoId: notificationData.convoId,
            url: `/admin/convo/inbox/${notificationData.convoId}`,
          },
        };

        console.log('[Notifications] Showing notification:', notificationTitle);
        const notification = new Notification(notificationTitle, notificationOptions);

        // Handle notification click - focus window and navigate
        notification.onclick = () => {
          window.focus();
          window.location.href = `/admin/convo/inbox/${notificationData.convoId}`;
          notification.close();
        };
      }
    });

    // Error events
    socket.on('error', (error: { message: string }) => {
      console.error('[Socket.io] Server error:', error.message);
    });

    return () => {
      console.log('[Socket.io] Cleaning up connection');
      socket.off('connect');
      socket.off('disconnect');
      socket.off('connect_error');
      socket.off('reconnect');
      socket.off('reconnect_failed');
      socket.off('unread_status');
      socket.off('error');
      socket.disconnect();
    };
  }, [user?.userId]);

  // Handle tab visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (socketRef.current && socketRef.current.connected) {
        if (!document.hidden) {
          // Tab became visible - request fresh status
          console.log('[Socket.io] Tab visible, requesting fresh status');
          socketRef.current.emit('get_unread_status');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Expose method to manually refresh
  const refresh = useCallback(() => {
    if (socketRef.current && socketRef.current.connected) {
      console.log('[Socket.io] Manual refresh requested');
      socketRef.current.emit('get_unread_status');
    } else {
      console.warn('[Socket.io] Cannot refresh - not connected');
    }
  }, []);

  return { unread, connected, refresh };
}
